import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { CURRENT_LEGAL_VERSION, LEGAL_URLS } from '../config/legal';
import { AlertCircle } from 'lucide-react';

// Wait for this much inactivity before interrupting an actively-used session
// (only applies to the hourly token-refresh re-check, never to load/focus/sign-in).
const IDLE_MS = 12000;

export function LegalAcceptanceGate() {
  const { user, loading, signOut, mfaGate } = useAuth();
  const mfaGateRef = useRef(mfaGate);
  useEffect(() => { mfaGateRef.current = mfaGate; }, [mfaGate]);
  const [show, setShow] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const lastActivityRef = useRef<number>(Date.now());
  const pendingRef = useRef(false);
  const deferTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userIdRef = useRef<string | null>(null);

  // True if the user is behind on acceptance for the CURRENT (runtime) version.
  // Any read error returns false — fail open, so a transient failure never locks
  // anyone out; the next check re-evaluates.
  const isBehind = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data: cfg, error: cfgErr } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', 'legal_version')
        .maybeSingle();
      if (cfgErr || !cfg?.value) return false;
      const currentVersion = cfg.value;

      const { data, error } = await supabase
        .from('document_acceptances')
        .select('document_type')
        .eq('user_id', userId)
        .eq('document_version', currentVersion)
        .in('document_type', ['terms', 'privacy']);
      if (error) return false;

      const accepted = new Set((data || []).map((r) => r.document_type));
      return !(accepted.has('terms') && accepted.has('privacy'));
    } catch (err) {
      console.error('Legal acceptance check error:', err);
      return false;
    }
  }, []);

  const clearDeferTimer = () => {
    if (deferTimerRef.current) {
      clearInterval(deferTimerRef.current);
      deferTimerRef.current = null;
    }
  };

  // immediate=true → show at once (load / sign-in / tab-focus boundaries).
  // immediate=false → defer until the user is briefly idle (hourly token refresh).
  const runCheck = useCallback(async (immediate: boolean) => {
    const uid = userIdRef.current;
    if (!uid) return;
    const behind = await isBehind(uid);
    if (!behind) {
      pendingRef.current = false;
      clearDeferTimer();
      setShow(false);
      return;
    }
    if (immediate) {
      pendingRef.current = false;
      clearDeferTimer();
      setShow(true);
    } else {
      if (pendingRef.current) return;
      pendingRef.current = true;
      deferTimerRef.current = setInterval(() => {
        if (Date.now() - lastActivityRef.current >= IDLE_MS) {
          pendingRef.current = false;
          clearDeferTimer();
          setShow(true);
        }
      }, 3000);
    }
  }, [isBehind]);

  // Track activity so the deferred (mid-session) prompt waits for a pause.
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  // Boundary check: on mount and whenever the user becomes available. Stands
  // down entirely while the MFA gate is pending: an enrolled user's aal1
  // session cannot read document_acceptances (MFA restrictive policy), so a
  // check now would misread "no acceptance" and stack this overlay over the
  // MFA screens. When the gate clears, this effect re-runs and checks then.
  useEffect(() => {
    if (mfaGate !== 'none') {
      pendingRef.current = false;
      clearDeferTimer();
      setShow(false);
      return;
    }
    if (!loading && user) {
      runCheck(true);
    } else if (!user) {
      pendingRef.current = false;
      clearDeferTimer();
      setShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, mfaGate]);

  // Re-check on tab focus (immediate) and on token refresh / sign-in.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && userIdRef.current) {
        runCheck(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((authEvent) => {
      if (!userIdRef.current) return;
      if (mfaGateRef.current !== 'none') return; // MFA gate owns the screen
      if (authEvent === 'SIGNED_IN') runCheck(true);
      else if (authEvent === 'TOKEN_REFRESHED') runCheck(false);
    });

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      subscription.unsubscribe();
    };
  }, [runCheck]);

  useEffect(() => () => clearDeferTimer(), []);

  const handleAccept = async () => {
    if (!checked || !user) return;
    setSubmitting(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError('Your session has expired. Please sign in again.');
        setSubmitting(false);
        return;
      }
      const res = await fetch('/.netlify/functions/record-acceptance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          documentTypes: ['terms', 'privacy'],
          version: CURRENT_LEGAL_VERSION,
        }),
      });
      if (!res.ok) {
        setError('Something went wrong recording your acceptance. Please try again.');
        setSubmitting(false);
        return;
      }
      setShow(false);
      setChecked(false);
    } catch (err) {
      console.error('Legal acceptance submit error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm px-4" data-testid="legal-acceptance-gate">
      <div className="max-w-md w-full bg-white rounded-xl shadow-2xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Please review and accept to continue</h2>
        <p className="text-gray-600 text-sm mb-6">
          To keep using TrueLoad, please review and agree to our current Terms of Service and Privacy Policy.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex items-start gap-3 mb-6">
          <input
            id="legal-gate-agree"
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="legal-agree"
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="legal-gate-agree" className="text-sm text-gray-600 leading-snug">
            I have read and agree to the{' '}
            <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Terms of Service</a>
            {' '}and{' '}
            <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</a>.
          </label>
        </div>

        <button
          onClick={handleAccept}
          disabled={!checked || submitting}
          data-testid="legal-accept"
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Saving...' : 'Agree and Continue'}
        </button>

        <button
          onClick={() => signOut()}
          data-testid="legal-signout"
          className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
